import './DatePicker.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { BiCaretDown } from 'react-icons/bi';
import { FaWrench } from 'react-icons/fa';
import { MdArrowForwardIos, MdCalendarToday, MdClose } from 'react-icons/md';
import ReactMultiDatePickerCjs from 'react-multi-date-picker';
import DatePanelCjs from 'react-multi-date-picker/plugins/date_panel';
import {
  CompanyFiscalCal,
  isOutsideFiscalYearRange
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  computeDateFromComparisonPreset,
  computeDateFromPreset,
  CustomPresetState,
  defaultIsOutsideRange,
  INIT_CUSTOM_PRESET_STATE,
  RangeErrorPreset
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useRenderedPosition } from '../../utils/hooks/useDOMHelpers';
import { useOnClose } from '../../utils/hooks/useOnClose';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import { Button } from '../Button';
import {
  Appearance,
  AppearanceType,
  BackgroundType,
  Design,
  DesignType,
  SizeType,
  Placement,
  Size,
  StretchModeType
} from '../designConstants';
import { IconButton } from '../IconButton';
import { ToggleSwitch } from '../ToggleSwitch';
import { InteractiveDiv } from '../InteractiveEl';
import { DateRange } from './date-picker-types';
import { DateInputField } from './DateInputField/DateInputField';
import { DatePickerButton } from './DatePickerButton';
import {
  CALENDAR_HEIGHT,
  CALENDAR_SPACE,
  CALENDAR_WIDTH,
  COMP_CALENDAR_HEIGHT,
  DATE_INTERVALS,
  DEFAULT_IS_DAYBLOCKED,
  DEFAULT_MAX_YEAR,
  DEFAULT_MIN_YEAR,
  FOCUS_OPTIONS,
  GRAPH_INTERVALS_WIDTH,
  PRESETS_WIDTH
} from './DatePickerButton/DatePickerConstants';
import { DPCalendar } from './DPCalendar';
import { DPPresets } from './DPPresets';
import { ToolbarSizeReset } from '../Toolbar';

export interface DateConfig {
  // Primary
  start: string | null;
  end: string | null;
  selectedPreset: string | null;
  startCustomPreset: CustomPresetState;
  endCustomPreset: CustomPresetState;
  // Comparison
  comparisonStart?: string | null;
  comparisonEnd?: string | null;
  selectedCompPerType?: string | null;
  startCompCustomPreset?: CustomPresetState;
  endCompCustomPreset?: CustomPresetState;
}

interface SingleDatePickerProps {
  range?: DateRange;
  onChange: (
    newDates: DateRange,
    {
      selectedPreset,
      startCustomPreset,
      endCustomPreset
    }: {
      selectedPreset: string | null;
      startCustomPreset: CustomPresetState;
      endCustomPreset: CustomPresetState;
    }
  ) => void;
  minYear?: number;
  maxYear?: number;
  isOutsideRange?: (date: string) => boolean;
  isDayBlocked?: (date: string, focus?: (typeof FOCUS_OPTIONS)[number]) => boolean;
  bordered?: boolean;
  currentMonthSecond?: boolean;
  fullWeeksOnly?: boolean; // Used for linear pages in which we only allow selection of entire weeks starting on Mondays.
  exclusiveEndWeek?: boolean; // With fullWeeksOnly, render the end as the prior Sunday (end Monday is exclusive) and drop its "Week of" prefix.
  // Renders a year-of-months grid instead of the day calendar; each click selects a whole month.
  // `range.end` is a month-START string like every other value here (not the last day of that
  // month) — callers needing an actual end-of-month value convert it themselves. Free-text date
  // entry and day-level `datePresets` are hidden in this mode since neither respects month
  // boundaries.
  fullMonthsOnly?: boolean;
  maxDate?: string; // Adds tooltip on hover to disabled dates. Also used for updating the date range when a preset is selected.
  minDate?: string; // Used for updating the date range when a preset is selected.
  dateInterval?: boolean;
  dateIntervalOnChange?: (param: string) => void;
  datePresets?: boolean;
  removeHeaderText?: boolean;
  fiscalCalOverride?: CompanyFiscalCal; // pass in an empty object for a standard calendar
  design?: DesignType;
  label?: string;
  background?: BackgroundType;
  appearance?: AppearanceType;
  disabled?: boolean;
  hasError?: boolean;
  stretch?: StretchModeType;
  /** See DatePickerButton: collapse to the calendar icon alone while disabled. Opt-in. */
  iconOnlyWhenLinked?: boolean;
  /** See DatePickerButton: collapse to the calendar icon but stay clickable. For tight layouts. */
  iconOnly?: boolean;
  /** See DatePickerButton: extra class on the collapsed state's hover tooltip. */
  tooltipClassName?: string;
  additionalRightHeaderItems?: React.ReactNode;
  // Render-prop for caller-supplied presets shown in the `.controls` column to the RIGHT
  // of the calendar (mirrors the old app's `customPresets`). Distinct from the built-in
  // `datePresets`; use when the caller owns its own preset UI (e.g. ScenarioPlanningPresets).
  customPresets?: (args: {
    setDates: (start: string, end: string) => void;
    start?: string;
    end?: string;
  }) => React.ReactNode;
  onApply?: () => void;
  allowOnlyStartDates?: boolean;
  // Single-date mode: pick exactly one date. Renders a single unlabeled date
  // field (no start/end) and hides the "Primary Date Range" header by default.
  isSingle?: boolean;
  // Months shown side by side in the primary calendar of the picker popup (default 3); the
  // comparison calendar always shows 3.
  numVisibleMonths?: number;
  // Hide the popup header (typed date entry), leaving just the calendar to click.
  hideDateInput?: boolean;
  // Render a clear (×) inside the open picker so the value can be reset to empty.
  // onChange fires with an empty range (EMPTY_RANGE).
  isClearable?: boolean;
  // Text shown on the collapsed button when no date is selected. The default is
  // resolved by the DatePicker parent: "Select Date" in single mode, "Select Dates" otherwise.
  placeholder?: string;
  // Initialize date picker to this config if provided
  dateConfig?: DateConfig;
  // onChange that includes inputs from both primary and comparison
  primaryAndComparisonOnChange?: ({
    start,
    end,
    selectedPreset,
    startCustomPreset,
    endCustomPreset,
    comparisonStart,
    comparisonEnd,
    selectedCompPerType,
    startCompCustomPreset,
    endCompCustomPreset
  }: DateConfig) => void;
  size?: SizeType;
  onClose?: () => void;
  // If true, the picker overlay starts open. Intended for Storybook stories so
  // Chromatic can snapshot the calendar UI; not for production use.
  defaultShow?: boolean;
}

interface ComparisonDatePickerProps extends SingleDatePickerProps {
  comparison: true;
  comparisonRange: DateRange | undefined;
  comparisonOnChange: (
    newDates: DateRange,
    {
      selectedCompPerType,
      startCompCustomPreset,
      endCompCustomPreset
    }: {
      selectedCompPerType: string | null;
      startCompCustomPreset: CustomPresetState;
      endCompCustomPreset: CustomPresetState;
    }
  ) => void;
  disableComparisonSetter?: (newVal: boolean) => void;
}

type RangeDatePickerProps =
  | (SingleDatePickerProps & {
      comparisonRange?: never;
      comparisonOnChange?: never;
      comparison?: false;
      disableComparisonSetter?: never;
    })
  | ComparisonDatePickerProps;

interface MultiDatePickerProps {
  multiDate: true;
  value: string[];
  onChange: (dates: string[]) => void;
  placeholder?: string;
}

type DatePickerProps = (RangeDatePickerProps & { multiDate?: false }) | MultiDatePickerProps;

const MULTI_DATE_FORMAT = 'YYYY-MM-DD';

const COMPARISON_HEADER_LABEL = 'Comparison Date Range';

// Empty single/range value; used to reset a clearable picker.
const EMPTY_RANGE: DateRange = { start: '', end: '' };

// react-multi-date-picker is published as CJS (CommonJS) without an `exports` map, so Vite returns
// each subpath as a `{ default: ... }` namespace object rather than unwrapping the default.
// Unwrap manually to get the actual component functions.
const unwrapCjsDefault = <T,>(mod: T): T => (mod as { default?: T }).default ?? mod;
const ReactMultiDatePicker = unwrapCjsDefault(ReactMultiDatePickerCjs);
const DatePanel = unwrapCjsDefault(DatePanelCjs);

interface MultiDatePickerTriggerProps {
  count: number;
  onClick: () => void;
  placeholder: string;
}

const MultiDatePickerTrigger = ({ count, onClick, placeholder }: MultiDatePickerTriggerProps) => {
  let label = placeholder;
  if (count > 0) {
    label = `${conditionallyPluralizeWithCount(count, 'date')} selected`;
  }
  return (
    <Button
      size={Size.LG}
      className='date-picker-button'
      onClick={onClick}
      leadingElement={<MdCalendarToday />}
    >
      <div className='dp-range-and-caret'>
        <div className='dpb-range'>{label}</div>
        <BiCaretDown />
      </div>
    </Button>
  );
};

const MultiDatePicker = ({
  value,
  onChange,
  placeholder
}: Omit<MultiDatePickerProps, 'multiDate' | 'placeholder'> & { placeholder: string }) => (
  <ReactMultiDatePicker
    multiple
    format={MULTI_DATE_FORMAT}
    value={value}
    onChange={dates => {
      if (!Array.isArray(dates)) {
        return;
      }
      onChange(dates.map(date => date.format(MULTI_DATE_FORMAT)));
    }}
    render={(_, openCalendar) => (
      <MultiDatePickerTrigger
        count={value.length}
        onClick={openCalendar}
        placeholder={placeholder}
      />
    )}
    plugins={[<DatePanel key='date-panel' />]}
    className='multi-date-picker'
  />
);

export const DatePicker = ({ ...modeProps }: DatePickerProps) => {
  // The parent owns placeholder defaulting so it lives in a single place.
  if (modeProps.multiDate) {
    const { value, onChange, placeholder = 'Select Dates' } = modeProps;
    return <MultiDatePicker value={value} onChange={onChange} placeholder={placeholder} />;
  }
  const placeholder =
    modeProps.placeholder ?? (modeProps.isSingle ? 'Select Date' : 'Select Dates');
  return <RangeDatePicker {...modeProps} placeholder={placeholder} />;
};

const RangeDatePicker = ({
  range,
  onChange,
  minYear = DEFAULT_MIN_YEAR,
  maxYear = DEFAULT_MAX_YEAR,
  isOutsideRange = defaultIsOutsideRange,
  isDayBlocked = DEFAULT_IS_DAYBLOCKED,
  currentMonthSecond,
  fullWeeksOnly,
  exclusiveEndWeek,
  fullMonthsOnly = false,
  label,
  maxDate,
  minDate,
  dateInterval = false,
  datePresets = false,
  comparison = false,
  comparisonRange,
  comparisonOnChange,
  dateIntervalOnChange: _dateIntervalOnChange,
  disableComparisonSetter,
  removeHeaderText = false,
  fiscalCalOverride,
  design = Design.BRAND,
  background,
  appearance = Appearance.FILLED,
  disabled = false,
  hasError = false,
  stretch,
  iconOnlyWhenLinked = false,
  iconOnly = false,
  tooltipClassName,
  additionalRightHeaderItems,
  customPresets,
  onApply,
  allowOnlyStartDates,
  isSingle = false,
  numVisibleMonths,
  hideDateInput = false,
  isClearable = false,
  placeholder,
  dateConfig,
  primaryAndComparisonOnChange,
  size,
  onClose,
  defaultShow = false
}: RangeDatePickerProps) => {
  // Years for the year picker
  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => i + minYear),
    [minYear, maxYear]
  );
  const { start, end } = range || {};
  const { start: comparisonStart, end: comparisonEnd } = comparisonRange || {};
  const ref = useRef<HTMLButtonElement>(null);
  const [position, resetPosition] = useRenderedPosition(ref);
  const [show, setShowRaw] = useState(defaultShow);

  useOnClose(show, onClose);
  const { fiscal_cal, cid } = useCompanyInfo();
  const fiscalCal = useMemo(() => fiscalCalOverride || fiscal_cal, [fiscalCalOverride, fiscal_cal]);
  const [dateInputs, setDateInputs] = useState<Partial<DateRange>>({
    start,
    end
  });
  const [comparisonDateInputs, setComparisonDateInputs] = useState<Partial<DateRange>>({
    start: comparisonStart,
    end: comparisonEnd
  });

  // Derived state during render: sync dateInputs when props change
  const [prevStart, setPrevStart] = useState(start);
  const [prevEnd, setPrevEnd] = useState(end);
  if (prevStart !== start || prevEnd !== end) {
    setPrevStart(start);
    setPrevEnd(end);
    setDateInputs(prev => {
      const updated = { ...prev };
      let changed = false;
      if (prev.start !== start) {
        updated.start = start;
        changed = true;
      }
      if (prev.end !== end) {
        updated.end = end;
        changed = true;
      }
      return changed ? updated : prev;
    });
  }

  // Derived state during render: sync comparisonDateInputs when props change
  const [prevCompStart, setPrevCompStart] = useState(comparisonStart);
  const [prevCompEnd, setPrevCompEnd] = useState(comparisonEnd);
  if (prevCompStart !== comparisonStart || prevCompEnd !== comparisonEnd) {
    setPrevCompStart(comparisonStart);
    setPrevCompEnd(comparisonEnd);
    setComparisonDateInputs(prev => {
      const updated = { ...prev };
      let changed = false;
      if (prev.start !== comparisonStart) {
        updated.start = comparisonStart;
        changed = true;
      }
      if (prev.end !== comparisonEnd) {
        updated.end = comparisonEnd;
        changed = true;
      }
      return changed ? updated : prev;
    });
  }
  const [focus, setFocus] = useState<(typeof FOCUS_OPTIONS)[number]>('START');
  const [comparisonFocus, setComparisonFocus] = useState<(typeof FOCUS_OPTIONS)[number]>('START');
  const [selectedInterval, setSelectedInterval] = useState<
    (typeof DATE_INTERVALS)[number] | null
  >();
  const [selectedPreset, setSelectedPreset] = useState(
    dateConfig ? dateConfig.selectedPreset : null
  );
  const [selectedCompPerType, setSelectedCompPerType] = useState(
    dateConfig && dateConfig.selectedCompPerType ? dateConfig.selectedCompPerType : null
  );
  const [comparisonEnabled, setComparisonEnabled] = useState(true);
  const [startCustomPreset, setStartCustomPreset] = useState(
    dateConfig && dateConfig.selectedPreset === 'Custom' && dateConfig.startCustomPreset
      ? dateConfig.startCustomPreset
      : {
          ...INIT_CUSTOM_PRESET_STATE
        }
  );
  const [endCustomPreset, setEndCustomPreset] = useState(
    dateConfig && dateConfig.selectedPreset === 'Custom' && dateConfig.endCustomPreset
      ? dateConfig.endCustomPreset
      : {
          ...INIT_CUSTOM_PRESET_STATE
        }
  );
  const [startCompCustomPreset, setStartCompCustomPreset] = useState(
    dateConfig && dateConfig.startCompCustomPreset
      ? dateConfig.startCompCustomPreset
      : {
          ...INIT_CUSTOM_PRESET_STATE
        }
  );
  const [endCompCustomPreset, setEndCompCustomPreset] = useState(
    dateConfig && dateConfig.endCompCustomPreset
      ? dateConfig.endCompCustomPreset
      : {
          ...INIT_CUSTOM_PRESET_STATE
        }
  );
  const [compOpen, setCompOpen] = useState(false);
  const [rangeErrorPreset, setRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [compRangeErrorPreset, setCompRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [firstUserChange, setFirstUserChange] = useState([0, 0]);

  const resolvedPosition = useMemo(() => {
    // Start off anchored below the input aligned on the right
    let controlsWidth = 0;
    if (datePresets) {
      controlsWidth = PRESETS_WIDTH;
    } else if (dateInterval) {
      controlsWidth = GRAPH_INTERVALS_WIDTH;
    }
    const combinedWidth = CALENDAR_WIDTH + controlsWidth;
    const combinedHeight = comparison ? COMP_CALENDAR_HEIGHT : CALENDAR_HEIGHT;
    let top = position.bottom + CALENDAR_SPACE;
    let left = position.right - combinedWidth;
    const overlayStyle: { height?: string; width?: string } = {};
    // If it spills off the bottom of the page, open up
    if (top + combinedHeight > window.innerHeight) {
      top = CALENDAR_SPACE;
      if (top + combinedHeight > window.innerHeight) {
        overlayStyle.height = `${top + combinedHeight + CALENDAR_SPACE}px`;
      }
    }
    // search for a better spot
    if (left < 0) {
      ({ left } = position);
      if (left + combinedWidth > window.innerWidth) {
        left = window.innerWidth / 2 - combinedWidth / 2;
        if (left + combinedWidth > window.innerWidth) {
          left = CALENDAR_SPACE;
          if (left + combinedWidth > window.innerWidth) {
            overlayStyle.width = `${left + combinedWidth + CALENDAR_SPACE}px`;
          }
        }
      }
    }
    return { position: { top, left }, overlay: overlayStyle };
  }, [position, dateInterval, datePresets, comparison]);

  const resolvedIsOutsideValidRange = useCallback(
    (date: string) => {
      const invalidFormat = !Dfns.isValid(Dfns.parseISO(date));
      if (invalidFormat) {
        return true;
      }

      const outsideYearRange = isOutsideFiscalYearRange(
        date,
        years[0],
        years[years.length - 1],
        fiscalCal
      );
      return isOutsideRange(date) || isDayBlocked(date) || outsideYearRange || invalidFormat;
    },
    [isOutsideRange, isDayBlocked, years, fiscalCal]
  );
  // okay need to add an override of fiscal cal
  const resetState = useCallback(() => {
    setSelectedInterval(null);
    setSelectedPreset(null);
    setSelectedCompPerType(null);
    setStartCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setEndCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setStartCompCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setEndCompCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setCompOpen(false);
    setRangeErrorPreset(null);
    setCompRangeErrorPreset(null);
  }, []);

  const setShow = useCallback(
    (shouldShow: boolean, save?: boolean) => {
      resetPosition();
      setShowRaw(shouldShow);
      if (!shouldShow && dateInputs.start && (dateInputs.end || allowOnlyStartDates || isSingle)) {
        setFocus('START');
        setDateInputs({ start, end });
        if (comparison) {
          setComparisonDateInputs({
            start: comparisonStart,
            end: comparisonEnd
          });
          if (disableComparisonSetter) {
            disableComparisonSetter(!comparisonEnabled);
          }
          setComparisonEnabled(true);
        }
        let mixpanelLogPrimary = false;
        let mixpanelLogComparison = false;

        if (save && (dateInputs.start !== start || dateInputs.end !== end)) {
          onChange(dateInputs as DateRange, {
            selectedPreset,
            startCustomPreset,
            endCustomPreset
          });
          mixpanelLogPrimary = true;
        }

        if (
          comparison &&
          save &&
          comparisonOnChange &&
          comparisonDateInputs &&
          (comparisonDateInputs.start !== comparisonStart ||
            comparisonDateInputs.end !== comparisonEnd)
        ) {
          comparisonOnChange(comparisonDateInputs as DateRange, {
            selectedCompPerType,
            startCompCustomPreset,
            endCompCustomPreset
          });
          mixpanelLogComparison = true;
        }

        if (save && primaryAndComparisonOnChange) {
          primaryAndComparisonOnChange({
            start: dateInputs?.start || '',
            end: dateInputs?.end || '',
            selectedPreset,
            startCustomPreset,
            endCustomPreset,
            comparisonStart: comparisonDateInputs?.start || '',
            comparisonEnd: comparisonDateInputs?.end || '',
            selectedCompPerType,
            startCompCustomPreset,
            endCompCustomPreset
          });
        }

        if (mixpanelLogPrimary && mixpanelLogComparison) {
          Mixpanel.track(MxE.DateChange, { primary_or_comparison: 'both' });
        } else if (mixpanelLogPrimary) {
          Mixpanel.track(MxE.DateChange, { primary_or_comparison: 'primary' });
        } else if (mixpanelLogComparison) {
          Mixpanel.track(MxE.DateChange, { primary_or_comparison: 'comparison' });
        }
      } else if (shouldShow && disableComparisonSetter) {
        disableComparisonSetter(false);
      }
      if (!shouldShow) {
        resetState();
      } else {
        setFirstUserChange([0, 0]);
        // Week-range picker always opens ready to pick a start (Monday); otherwise a stale
        // END focus (e.g. from the end-date span click) would block Mondays on open.
        if (exclusiveEndWeek) {
          setFocus('START');
        }
      }
    },
    [
      resetPosition,
      dateInputs,
      allowOnlyStartDates,
      isSingle,
      exclusiveEndWeek,
      disableComparisonSetter,
      start,
      end,
      comparison,
      comparisonOnChange,
      comparisonDateInputs,
      comparisonStart,
      comparisonEnd,
      comparisonEnabled,
      onChange,
      selectedPreset,
      startCustomPreset,
      endCustomPreset,
      selectedCompPerType,
      startCompCustomPreset,
      endCompCustomPreset,
      resetState,
      primaryAndComparisonOnChange
    ]
  );

  const onChangeInternal = useCallback(
    (dateRange: Partial<DateRange>, newFocus: (typeof FOCUS_OPTIONS)[number]) => {
      setDateInputs(dateRange);
      setFocus(newFocus);
      setSelectedPreset(null);
    },
    []
  );

  // Clear the current value and reset presets without opening the calendar.
  const onClear = useCallback(() => {
    setDateInputs(EMPTY_RANGE);
    setSelectedPreset(null);
    setStartCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    setEndCustomPreset({ ...INIT_CUSTOM_PRESET_STATE });
    onChange(EMPTY_RANGE, {
      selectedPreset: null,
      startCustomPreset: { ...INIT_CUSTOM_PRESET_STATE },
      endCustomPreset: { ...INIT_CUSTOM_PRESET_STATE }
    });
  }, [onChange]);

  const onChangeComparisonInternal = useCallback(
    (dateRange: Partial<DateRange>, newFocus: (typeof FOCUS_OPTIONS)[number]) => {
      setComparisonDateInputs(dateRange);
      setComparisonFocus(newFocus);
      setSelectedCompPerType(null);
    },
    []
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShow(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [setShow]);

  /*
   * Uses provided minDate and maxDate to override the computed date range from preset when invalid
   * dates are included in the range.
   */
  const onDatePresetSelect = useCallback(
    (preset: string | null) => {
      try {
        const newRange = computeDateFromPreset(preset, fiscalCal);
        const newStart =
          minDate && newRange?.start && newRange?.start < minDate ? minDate : newRange?.start;
        const newEnd =
          maxDate && newRange?.end && newRange?.end > maxDate ? maxDate : newRange?.end;
        setDateInputs(newStart && newEnd ? { start: newStart, end: newEnd } : { start, end });
      } catch {
        console.warn('Error computing date from comparison preset');
      }
    },
    [fiscalCal, minDate, maxDate, start, end]
  );

  // Derived state during render: track user changes for auto-default comparison
  const [prevChangeTracking, setPrevChangeTracking] = useState({
    dateStart: dateInputs.start,
    dateEnd: dateInputs.end,
    propStart: start,
    propEnd: end,
    compDateStart: comparisonDateInputs.start,
    compDateEnd: comparisonDateInputs.end,
    propCompStart: comparisonStart,
    propCompEnd: comparisonEnd
  });

  if (
    prevChangeTracking.dateStart !== dateInputs.start ||
    prevChangeTracking.dateEnd !== dateInputs.end ||
    prevChangeTracking.propStart !== start ||
    prevChangeTracking.propEnd !== end ||
    prevChangeTracking.compDateStart !== comparisonDateInputs.start ||
    prevChangeTracking.compDateEnd !== comparisonDateInputs.end ||
    prevChangeTracking.propCompStart !== comparisonStart ||
    prevChangeTracking.propCompEnd !== comparisonEnd
  ) {
    setPrevChangeTracking({
      dateStart: dateInputs.start,
      dateEnd: dateInputs.end,
      propStart: start,
      propEnd: end,
      compDateStart: comparisonDateInputs.start,
      compDateEnd: comparisonDateInputs.end,
      propCompStart: comparisonStart,
      propCompEnd: comparisonEnd
    });

    let newPrimary = firstUserChange[0];
    let newComp = firstUserChange[1];
    let changed = false;

    if (
      dateInputs.start &&
      dateInputs.end &&
      (dateInputs.start !== start || dateInputs.end !== end)
    ) {
      if (newPrimary < 2) {
        newPrimary += 1;
        changed = true;
      }
    } else if (dateInputs.start === start && dateInputs.end === end) {
      if (newPrimary !== 0) {
        newPrimary = 0;
        changed = true;
      }
    }

    if (
      comparisonDateInputs.start &&
      comparisonDateInputs.end &&
      (comparisonDateInputs.start !== comparisonStart || comparisonDateInputs.end !== comparisonEnd)
    ) {
      if (newComp < 2) {
        newComp += 1;
        changed = true;
      }
    }

    if (changed) {
      setFirstUserChange([newPrimary, newComp]);
      if (newPrimary === 1 && !newComp) {
        setSelectedCompPerType('Default');
      }
    }
  }

  // Derived state during render: compute comparison dates from preset
  const [prevCompPresetDeps, setPrevCompPresetDeps] = useState({
    dateStart: dateInputs.start,
    dateEnd: dateInputs.end,
    selectedCompPerType,
    fiscalCal
  });

  if (
    prevCompPresetDeps.dateStart !== dateInputs.start ||
    prevCompPresetDeps.dateEnd !== dateInputs.end ||
    prevCompPresetDeps.selectedCompPerType !== selectedCompPerType ||
    prevCompPresetDeps.fiscalCal !== fiscalCal
  ) {
    setPrevCompPresetDeps({
      dateStart: dateInputs.start,
      dateEnd: dateInputs.end,
      selectedCompPerType,
      fiscalCal
    });

    const inputStart = dateInputs.start;
    const inputEnd = dateInputs.end;
    if (inputStart && inputEnd && selectedCompPerType !== null) {
      try {
        const newRange = computeDateFromComparisonPreset(
          selectedCompPerType,
          inputStart,
          inputEnd,
          fiscalCal
        );
        if (newRange) {
          if (
            (newRange.start && resolvedIsOutsideValidRange(newRange.start)) ||
            (newRange.end && resolvedIsOutsideValidRange(newRange.end))
          ) {
            setSelectedCompPerType(null);
          } else {
            setComparisonDateInputs(newRange);
          }
        }
      } catch {
        console.warn('Error computing date from comparison preset');
      }
    }
  }

  // "Primary" only means something when there's also a comparison range to distinguish it from.
  let primaryHeaderTitle = '';
  if (!removeHeaderText && !isSingle) {
    primaryHeaderTitle = comparison ? 'Primary Date Range' : 'Select Date Range';
  }

  return (
    <>
      <DatePickerButton
        reference={ref}
        setShow={setShow}
        range={range}
        fullWeeksOnly={fullWeeksOnly}
        exclusiveEndWeek={exclusiveEndWeek}
        setFocus={setFocus}
        comparison={comparison}
        comparisonRange={comparisonRange}
        background={background}
        disabled={disabled}
        hasError={hasError}
        stretch={stretch}
        isOpen={show}
        iconOnlyWhenLinked={iconOnlyWhenLinked}
        iconOnly={iconOnly}
        tooltipClassName={tooltipClassName}
        appearance={appearance}
        design={design}
        label={label}
        size={size}
        isSingle={isSingle}
        placeholder={placeholder}
        fullMonthsOnly={fullMonthsOnly}
      />
      <ToolbarSizeReset>
        {show &&
          createPortal(
            <div className='dp-date-container'>
              <div
                role='presentation'
                className='overlay'
                onClick={() => setShow(false)}
                style={resolvedPosition.overlay}
              />
              <div className='calendar-box' style={resolvedPosition.position}>
                {!hideDateInput && (
                  <div className='dp-header'>
                    <div className='header-title-text'>{primaryHeaderTitle}</div>
                    <div className='right-header-items'>
                      {additionalRightHeaderItems}
                      <div className='date-display'>
                        {!fullMonthsOnly &&
                          (isSingle ? (
                            <DateInputField
                              label=''
                              value={dateInputs.start || ''}
                              setValue={(value: string) =>
                                setDateInputs({ start: value, end: value })
                              }
                              invalidateDateOrRange={resolvedIsOutsideValidRange}
                              clearPreset={() => setSelectedPreset(null)}
                            />
                          ) : (
                            <>
                              <DateInputField
                                label='Start'
                                value={dateInputs.start || ''}
                                setValue={(value: string) => {
                                  setDateInputs(d =>
                                    !d.end || value > d.end
                                      ? { start: value }
                                      : { ...d, start: value }
                                  );
                                }}
                                invalidateDateOrRange={resolvedIsOutsideValidRange}
                                clearPreset={() => setSelectedPreset(null)}
                              />
                              <DateInputField
                                label='End'
                                value={dateInputs.end || ''}
                                setValue={(value: string) => {
                                  setDateInputs(d =>
                                    !d.start || value < d.start
                                      ? { end: value }
                                      : { ...d, end: value }
                                  );
                                }}
                                invalidateDateOrRange={resolvedIsOutsideValidRange}
                                clearPreset={() => setSelectedPreset(null)}
                              />
                            </>
                          ))}
                        {isClearable && (dateInputs.start || dateInputs.end) && (
                          <IconButton
                            appearance={Appearance.EMPTY}
                            design={Design.NEUTRAL}
                            className='dp-clear'
                            aria-label='Clear date'
                            onClick={() => {
                              onClear();
                              setShow(false);
                            }}
                          >
                            <MdClose />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className='calendar-and-controls primary'>
                  <DPCalendar
                    key='primaryDPCalendar'
                    start={dateInputs.start}
                    end={dateInputs.end}
                    focus={focus}
                    onChange={onChangeInternal}
                    isOutsideRange={isOutsideRange}
                    isDayBlocked={isDayBlocked}
                    years={years}
                    currentMonthSecond={currentMonthSecond}
                    maxDate={maxDate}
                    fiscalCalOverride={fiscalCalOverride}
                    isSingle={isSingle}
                    numVisibleMonths={numVisibleMonths}
                    fullMonthsOnly={fullMonthsOnly}
                  />
                  {(datePresets || dateInterval || customPresets) && (
                    <div className='controls'>
                      {customPresets?.({
                        setDates: (startInput: string, endInput: string) =>
                          setDateInputs({ start: startInput, end: endInput }),
                        start: dateInputs.start,
                        end: dateInputs.end
                      })}
                      {datePresets && !fullMonthsOnly && (
                        <div className='presets-container single-control-container'>
                          <div className='control-section-label'>
                            <div className='section-label-wrapper'>
                              Presets <span>(Optional)</span>
                            </div>
                          </div>
                          <DPPresets
                            setSelectedPreset={preset =>
                              setSelectedPreset(p => {
                                const setValue = p === preset ? null : preset;
                                onDatePresetSelect(setValue);
                                return setValue;
                              })
                            }
                            selectedPreset={selectedPreset}
                            setRangeErrorPreset={setRangeErrorPreset}
                            setDateInputs={setDateInputs}
                            resolvedIsOutsideValidRange={resolvedIsOutsideValidRange}
                            startCustomPreset={startCustomPreset}
                            setStartCustomPreset={setStartCustomPreset}
                            endCustomPreset={endCustomPreset}
                            setEndCustomPreset={setEndCustomPreset}
                            rangeErrorPreset={rangeErrorPreset}
                            fiscalCal={fiscalCal}
                            fiscalCalOverride={fiscalCalOverride}
                          />
                        </div>
                      )}
                      {dateInterval && (
                        <div className='single-control-container'>
                          <div className='control-section-label'>Graph Intervals</div>
                          {DATE_INTERVALS.map((interval, i) => (
                            <Button
                              key={`DPIntervalOptionButton${i}`}
                              appearance={Appearance.EMPTY}
                              design={Design.NEUTRAL}
                              onClick={() => {
                                setSelectedInterval(prev =>
                                  prev === interval ? undefined : interval
                                );
                              }}
                              className={cn('control-button', {
                                selected: selectedInterval === interval
                              })}
                            >
                              {interval}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {comparison && comparisonDateInputs && (
                  <>
                    <div
                      className={cn('com-header-wrapper top', {
                        'comp-open': compOpen,
                        'comp-closed': !compOpen
                      })}
                    />
                    <div
                      className={cn('dp-header comparison-header', {
                        'comp-head-disabled': !comparisonEnabled
                      })}
                    >
                      <div className='header-title-text'>
                        {disableComparisonSetter ? (
                          <ToggleSwitch
                            label={COMPARISON_HEADER_LABEL}
                            checked={comparisonEnabled}
                            onChange={setComparisonEnabled}
                          />
                        ) : (
                          COMPARISON_HEADER_LABEL
                        )}
                      </div>
                      <div className='right-header-items'>
                        <InteractiveDiv
                          className={cn('date-display comparison', {
                            'comparison-disabled': !comparisonEnabled
                          })}
                          onClick={() => setCompOpen(true)}
                        >
                          <DateInputField
                            label='Comparison Start'
                            value={comparisonDateInputs.start || ''}
                            setValue={(value: string) => {
                              setComparisonDateInputs(d =>
                                !d.end || value < d.end ? { ...d, start: value } : { start: value }
                              );
                            }}
                            invalidateDateOrRange={resolvedIsOutsideValidRange}
                            dashBoarder
                            clearPreset={() => setSelectedCompPerType(null)}
                          />
                          <DateInputField
                            label='Comparison End'
                            value={comparisonDateInputs.end || ''}
                            setValue={(value: string) => {
                              setComparisonDateInputs(d =>
                                !d.start || value > d.start ? { ...d, end: value } : { end: value }
                              );
                            }}
                            invalidateDateOrRange={resolvedIsOutsideValidRange}
                            dashBoarder
                            clearPreset={() => setSelectedCompPerType(null)}
                          />
                        </InteractiveDiv>
                        <IconButton
                          className={cn('dp-arrow', {
                            'comp-disabled-arrow': !comparisonEnabled,
                            'comparison-arrow-closed': !compOpen
                          })}
                          appearance={Appearance.EMPTY}
                          onClick={() => {
                            setCompOpen(c => !c);
                          }}
                          disabled={!comparisonEnabled}
                        >
                          <MdArrowForwardIos />
                        </IconButton>
                      </div>
                    </div>
                    <div
                      className={cn('com-header-wrapper', {
                        'comp-closed': !compOpen,
                        'comparison-disabled': !comparisonEnabled
                      })}
                    />
                    <div
                      className={cn('calendar-and-controls comparison', {
                        'comparison-disabled': !comparisonEnabled,
                        'comparison-closed': !compOpen
                      })}
                    >
                      <DPCalendar
                        key='comparisonDPCalendar'
                        start={comparisonDateInputs.start}
                        end={comparisonDateInputs.end}
                        focus={comparisonFocus}
                        onChange={onChangeComparisonInternal}
                        isOutsideRange={isOutsideRange}
                        isDayBlocked={isDayBlocked}
                        years={years}
                        currentMonthSecond={currentMonthSecond}
                        maxDate={maxDate}
                        fiscalCalOverride={fiscalCalOverride}
                        comparison
                        fullMonthsOnly={fullMonthsOnly}
                      />
                      {(datePresets || dateInterval) && (
                        <div className='controls'>
                          {datePresets && !fullMonthsOnly && (
                            <div className='presets-container single-control-container'>
                              <div className='control-section-label'>
                                <div className='section-label-wrapper'>
                                  Presets <span>(Optional)</span>
                                </div>
                              </div>
                              <DPPresets
                                setSelectedPreset={preset =>
                                  setSelectedCompPerType(p => {
                                    let setValue: string | null = preset;
                                    if (p === preset) {
                                      // when we "unpress it" go back to original input dates
                                      setValue = null;
                                      setComparisonDateInputs({
                                        start: comparisonStart,
                                        end: comparisonEnd
                                      });
                                    }
                                    return setValue;
                                  })
                                }
                                selectedPreset={selectedCompPerType}
                                setRangeErrorPreset={setCompRangeErrorPreset}
                                setDateInputs={setComparisonDateInputs}
                                resolvedIsOutsideValidRange={resolvedIsOutsideValidRange}
                                startCustomPreset={startCompCustomPreset}
                                setStartCustomPreset={setStartCompCustomPreset}
                                endCustomPreset={endCompCustomPreset}
                                setEndCustomPreset={setEndCompCustomPreset}
                                rangeErrorPreset={compRangeErrorPreset}
                                fiscalCalOverride={fiscalCalOverride}
                                comparison
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
                <div className='dp-footer'>
                  {cid && fiscalCalOverride == null && (
                    <OverlayTrigger
                      placement={Placement.LEFT}
                      overlay={<Tooltip id='datePickerCalSettingstt'>Calendar settings</Tooltip>}
                    >
                      <a
                        className='wrench-to-calendar-button'
                        href={`${window.location.origin}/${cid}/calendar-input`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <IconButton appearance={Appearance.EMPTY} size={Size.SM}>
                          <FaWrench size={20} />
                        </IconButton>
                      </a>
                    </OverlayTrigger>
                  )}
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    design={Design.NEUTRAL}
                    onClick={() => setShow(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size={Size.LG}
                    onClick={() => {
                      setShow(false, true);
                      onApply?.();
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </ToolbarSizeReset>
    </>
  );
};
